import { useCallback, useMemo, useRef, useState } from 'react'
import slides from '../slides'

const useCarouselState = () => {
    const [activeIndex, _setActiveIndex] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)
    const inFlight = useRef(0)

    const slidesLen = slides.length

    const activeSlide = useMemo(() => slides[activeIndex], [activeIndex])

    const markAnimationStart = useCallback(() => {
        if (++inFlight.current === 1) setIsAnimating(true)
    }, [])

    const markAnimationComplete = useCallback(() => {
        inFlight.current = Math.max(0, inFlight.current - 1)
        if (inFlight.current === 0) setIsAnimating(false)
    }, [])

    /**
     * Animation guard for setting the active index.
     * Prevents changing the active index while an animation is in flight.
     */
    const setActiveIndex = useCallback((index: number) => {
        if (inFlight.current > 0) return
        _setActiveIndex(((index % slidesLen) + slidesLen) % slidesLen)
    }, [slidesLen])

    const goToNextSlide = useCallback(() => {
        if (inFlight.current > 0) return
        _setActiveIndex(i => (i + 1) % slidesLen)
    }, [slidesLen])

    const goToPrevSlide = useCallback(() => {
        if (inFlight.current > 0) return
        _setActiveIndex(i => (i - 1 + slidesLen) % slidesLen)
    }, [slidesLen])

    const isActiveSlide = useCallback((index: number) => index === activeIndex, [activeIndex])

    const animationHandlers = useMemo(
        () => ({
            onAnimationStart: markAnimationStart,
            onAnimationComplete: markAnimationComplete,
        }),
        [markAnimationStart, markAnimationComplete]
    )

    return {
        slides,
        activeIndex,
        activeSlide,
        goToNextSlide,
        goToPrevSlide,
        setActiveIndex,
        isActiveSlide,
        isAnimating,
        animationHandlers,
    }
}

export default useCarouselState
